import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

import { cn } from "@/shared/lib";
import { Skeleton } from "@/shared/ui/skeleton";

import { useHeroLookup } from "../../api/useHeroLookup";
import { useMatches } from "../../api/useMatches";
import { useStatsSummary } from "../../api/useStatsSummary";
import { currentStreak } from "../../lib/currentStreak";
import { formatPercent } from "../../lib/formatPercent";
import { winRateSeverity } from "../../lib/winRateSeverity";
import { RankTile } from "./RankTile";
import { SummaryTile } from "./SummaryTile";
import { WeakSpotTile } from "./WeakSpotTile";

const RECENT = msg({ message: "{count, plural, one {Last # match} other {Last # matches}}" });
const WIN_STREAK = msg({ message: "{count, plural, one {# win} other {# wins}}" });
const LOSS_STREAK = msg({ message: "{count, plural, one {# loss} other {# losses}}" });

const TILE_COUNT = 4;
const GRID = cn("grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-2.5");

const TileSkeleton = () => <Skeleton className="h-[104px] rounded-xl" />;

// Сводка над главной по макету: счёт последних матчей и доля побед за всё время, текущая
// серия, ранг и повторяющаяся ошибка. Первые две — по загруженным матчам, ранг и ошибка —
// из сводки игрока (быстрый разбор); пока она считается — заглушки. Нет ранга (только
// обычные матчи) или оценённых матчей — плитки нет. Матчей нет — сводки нет, состояние
// показывает список.
export const RecentSummary = () => {
  const { i18n, t } = useLingui();
  const lookup = useHeroLookup();
  const { query, items } = useMatches();
  const stats = useStatsSummary();
  const streak = currentStreak(items);

  if (query.isPending) {
    return (
      <div className={GRID}>
        {Array.from({ length: TILE_COUNT }, (_, index) => (
          <TileSkeleton key={index} />
        ))}
      </div>
    );
  }
  if (streak === null) {
    return null;
  }

  const summary = stats.data?.summary ?? null;
  const isSummaryPending = stats.isPending || stats.data?.status === "pending";
  const wins = items.filter((match) => match.won).length;
  const losses = items.length - wins;
  const winRate = formatPercent(wins / items.length, i18n.locale);
  const allTime =
    summary === null || summary.matches === 0
      ? null
      : formatPercent(summary.wins / summary.matches, i18n.locale);
  const streakHeroes = [...new Set(streak.matches.map((match) => match.hero_id))]
    .map((heroId) => lookup(heroId).name)
    .join(", ");

  return (
    <section aria-label={t`Summary`} className={GRID}>
      <SummaryTile
        severity={winRateSeverity(wins / items.length)}
        label={i18n._({ ...RECENT, values: { count: items.length } })}
        value={
          <>
            <span aria-hidden="true">
              {wins} – {losses}
            </span>
            <span className="sr-only">
              <Trans>
                Wins {wins}, losses {losses}
              </Trans>
            </span>
          </>
        }
        details={
          allTime === null ? <Trans>{winRate} wins</Trans> : t`${winRate} wins, all time ${allTime}`
        }
      />
      <SummaryTile
        severity={streak.won ? "good" : "bad"}
        label={<Trans>Streak</Trans>}
        value={i18n._({
          ...(streak.won ? WIN_STREAK : LOSS_STREAK),
          values: { count: streak.matches.length },
        })}
        details={streakHeroes}
      />
      {summary?.rank_badge != null ? (
        <RankTile badge={summary.rank_badge} weekAgo={summary.rank_badge_week_ago} />
      ) : (
        isSummaryPending && <TileSkeleton />
      )}
      {summary?.weak_spot != null && summary.weak_spot.matches > 0 ? (
        <WeakSpotTile weakSpot={summary.weak_spot} />
      ) : (
        isSummaryPending && <TileSkeleton />
      )}
    </section>
  );
};
